import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Toolbar from '@mui/material/Toolbar';
import { useTheme, useMediaQuery } from '@mui/material';
import Heading from 'common/Heading';
import SideNavBar from 'components/SideNavBar';
import DrawerIcon from 'common/Icons/DrawerIcon';
import styled from 'styled-components';

import React, { useState, useEffect } from 'react';

const ContainerDiv = styled.div`
    .MuiBox-root .MuiPaper-root {
        background-color: ${(props) => props.theme.colorWhite};
        color: ${(props) => props.theme.primaryFontColor};
    }

    .MuiDrawer-root .MuiDrawer-paper {
        background-color: ${(props) => props.theme.colorSecondary};
        color: ${(props) => props.theme.colorMenuFont};
    }

    .MuiBox-root {
        background-color: ${(props) => props.theme.colorBackground};
    }

    .selected {
        color: ${(props) => props.theme.colorWhite};
    }

    .MuiListItem-root {
        padding: 20px 20px;
    }

    .borderMenu {
    }
`;
const drawerWidth = 280;
const Dashboard = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    // State: open by default on desktop, closed on mobile
    const [navOpen, setNavOpen] = useState(!isMobile);
    const [isHovered, setIsHovered] = useState(false);
    
    // Update state when screen size changes
    useEffect(() => {
        setNavOpen(!isMobile);
    }, [isMobile]);
    
    const handleToggleNav = () => {
        setNavOpen(!navOpen);
        setIsHovered(false); // Reset hover state when toggling
    };
    
    const handleCloseNav = () => {
        setNavOpen(false);
        setIsHovered(false); // Reset hover state when closing
    };

    return (
        <ContainerDiv>
            <Box sx={{ display: 'flex' }}>
                <CssBaseline />

                <AppBar
                    position="fixed"
                    sx={{
                        width: isMobile ? '100%' : `calc(100% - ${navOpen ? drawerWidth : 0}px)`,
                        ml: isMobile ? 0 : (navOpen ? `${drawerWidth}px` : 0),
                        transition: 'width 0.3s ease, margin-left 0.3s ease',
                    }}
                >
                    <Toolbar>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            {/* Toggle button inside header when closed - only on mobile/tablet */}
                            {isMobile && !navOpen && (
                                <div
                                    className="flex items-center border border-gray-200 rounded-full cursor-pointer transition-colors"
                                    style={{
                                        marginTop: '0px', // Match open position - no extra margin
                                        backgroundColor: isHovered ? '#FDFAF7' : 'white', // Match account dropdown hover
                                        boxShadow: isHovered ? '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)' : 'none', // Subtle drop shadow on hover
                                        transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease', // Match Tailwind transition-colors
                                        flexShrink: 0, // Prevent button from shrinking
                                        minWidth: '40px', // Fixed minimum width
                                        minHeight: '40px', // Fixed minimum height
                                        padding: '8px', // Fixed padding - prevent shrinking
                                    }}
                                    onMouseEnter={() => setIsHovered(true)}
                                    onMouseLeave={() => setIsHovered(false)}
                                    onClick={handleToggleNav}
                                >
                                    <DrawerIcon 
                                        isOpen={navOpen} 
                                        isHovered={isHovered}
                                        style={{ 
                                            color: isHovered ? '#FF5F39' : '#184353',
                                            transition: 'color 100ms cubic-bezier(0.4, 0, 0.2, 1)'
                                        }}
                                    />
                                </div>
                            )}
                            <Heading>Dashboard</Heading>
                        </div>
                    </Toolbar>
                </AppBar>

                <SideNavBar isOpen={navOpen} onClose={handleCloseNav} />
                
                {/* Toggle button - only shown on tablet/mobile when nav is open (absolutely positioned) */}
                {isMobile && navOpen && (
                    <div
                        className="flex items-center border border-gray-200 rounded-full cursor-pointer transition-colors"
                        style={{
                            position: 'fixed',
                            top: '25px', // AppBar Toolbar padding (16px) + 9px margin
                            padding: '8px', // Fixed padding - prevent shrinking
                            // Mobile: position from left to half-overlap nav modal's right edge
                            // Modal right edge: 16px (margin) + min(drawerWidth, calc(100vw - 32px))
                            // Position button 20px to the left of modal's right edge for half-overlap
                            left: `calc(16px + min(${drawerWidth}px, calc(100vw - 32px)) - 20px)`,
                            right: 'auto',
                            zIndex: navOpen ? 1300 : 1200, // Above modal backdrop when modal open
                            backgroundColor: isHovered ? '#FDFAF7' : (navOpen ? 'rgba(245, 241, 237, 0.5)' : 'white'), // Account dropdown hover color when hovered, semi-transparent when open, white when closed
                            backdropFilter: navOpen && !isHovered ? 'blur(8px)' : 'none',
                            WebkitBackdropFilter: navOpen && !isHovered ? 'blur(8px)' : 'none',
                            borderColor: navOpen ? 'rgba(229, 231, 235, 0.5)' : 'rgba(229, 231, 235, 1)', // Semi-transparent border when open, solid when closed
                            boxShadow: navOpen && isHovered ? '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)' : 'none', // Subtle drop shadow on hover when open for floating effect
                            transition: 'left 0.3s ease, right 0.3s ease, background-color 150ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease',
                        }}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                        onClick={handleToggleNav}
                    >
                        <DrawerIcon 
                            isOpen={navOpen} 
                            isHovered={isHovered}
                            style={{ 
                                color: isHovered ? '#FF5F39' : '#184353',
                                transition: 'color 100ms cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                        />
                    </div>
                )}
                
                <Box 
                    component="main" 
                    sx={{ 
                        flexGrow: 1, 
                        bgcolor: 'background.default', 
                        p: 3,
                        ml: isMobile ? 0 : (navOpen ? `${drawerWidth}px` : 0),
                        transition: 'margin-left 0.3s ease'
                    }}
                >
                    <Toolbar />
                    WIP
                </Box>
            </Box>
            {/* <Notification message={notification} onClose={clearNotification} />*/}
        </ContainerDiv>
    );
};

export default Dashboard;
